/**
 * Collection landing page — `/collection/:slug`
 *
 * Themed bundle of curated stays. Editorial-register design with the same
 * fonts and tone as the valley pages, but the marketing angle is the
 * COLLECTION's pitch (e.g. families, couples-only, pet-friendly). Stays in
 * the grid link to their existing /stay/<slug> or /collective/<slug> page.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowRight, MessageCircle, Home, Mountain, Users, KeyRound,
  Flame, Fish, Trees, Sparkles as SparkIcon, Coffee, BookOpen, UtensilsCrossed, Bike,
  Sunrise, Bird, Music, Wine,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import EnquireModal from '../components/EnquireModal';

const API = process.env.REACT_APP_BACKEND_URL;

// Themed activity prompts per collection.angle. Each item now has an icon
// component so the "What you'll actually do" strip reads as a visual map of
// the week, not a wall of text. Keep these tight and specific.
const ANGLE_EXPERIENCES = {
  family: [
    { icon: KeyRound, hand: 'Privacy',
      title: 'No shared spaces',
      copy: 'You hold the only key. No other guests at breakfast, no roaming staff. The cottage is yours for the week.' },
    { icon: Flame,    hand: 'Evenings',
      title: 'Bonfires & marshmallows',
      copy: 'Most hosts will pile up dry pine and light it up after dinner. Cards, antaakshari, the works.' },
    { icon: UtensilsCrossed, hand: 'The food',
      title: 'Cooking with the host',
      copy: 'Local rajma, trout, bhang-ki-chutney, hot rotis off a chulha. Hands-on if the family wants in.' },
    { icon: Fish,     hand: 'River',
      title: 'Trout & dips',
      copy: 'Riverside picnics, shallow pools for the little ones, a guided trout cast for the brave.' },
    { icon: Trees,    hand: 'Walks',
      title: 'Forest, ridge, sunrise',
      copy: 'Short loops the kids can finish. Ridge sunrises for the grown-ups. Hosts hand you a map at check-in.' },
    { icon: Users,    hand: 'On the lawn',
      title: 'Games & cards',
      copy: 'Carrom, badminton, cricket on the lawn, a deck of cards. Evenings vanish before you notice.' },
    { icon: Bird,     hand: 'Slow mornings',
      title: 'Birdwatching & chai',
      copy: 'Coffee on the veranda, binoculars on the railing, a copy of Grimmett at the side. That kind of week.' },
    { icon: Sunrise,  hand: 'Pre-dawn',
      title: 'Sunrise rituals',
      copy: 'Some properties have a sunrise deck. Some hosts will wake you. Either way — it\u2019s worth the 5am alarm.' },
    { icon: BookOpen, hand: 'Indoors',
      title: 'Library + fireplace',
      copy: 'A wood-stove or chimney, a shelf of paperbacks, and absolutely no Wi-Fi pressure. Read like you used to.' },
    { icon: Music,    hand: 'Local culture',
      title: 'Village & melas',
      copy: 'A walk to the village temple, a school visit, or the seasonal mela if your dates line up.' },
  ],
};

const toFeaturedSlug = (name) => {
  if (!name) return '';
  const head = name.split(',')[0].trim().toLowerCase();
  return head.replace(/['\u2019]/g, '').replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '');
};

const _resolvePath = (stay) => {
  if (stay.partnership_status === 'partner' && stay.booking_source) {
    return `/collective/${stay.booking_source}`;
  }
  return `/stay/${toFeaturedSlug(stay.display_name)}`;
};

const _resolveImg = (stay) => {
  const img = (stay.images || [])[0];
  if (!img) return '';
  return typeof img === 'object' ? (img.url || img.src) : img;
};

/**
 * GoHimalayan mountain mark — single source of truth used by the tier badges.
 * Path matches the rule-book SVG in FeaturedStay.js so the symbol is identical
 * everywhere the brand appears.
 */
const MountainMark = ({ className = "w-3.5 h-3.5", strokeWidth = 2.4 }) => (
  <svg className={`${className} flex-shrink-0`} viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
    <path d="m8 3 4 8 5-5 5 15H2L8 3z"/>
  </svg>
);

/**
 * TierBadge — compact card-corner version of the official tier marks. Matches
 * the rule-book mapping in FeaturedStay.js:StayBadge.
 *   featured       → white pill, blue mountain (editorial top tier)
 *   partner        → green pill, white mountain (GoHimalayan Collective 100)
 *   valley_partner → white pill, green mountain (regional partnership)
 *   discovered     → off-white pill, sage mountain (vetted via editorial)
 *   scraped / unknown → nothing (keeps the card visually clean)
 */
const TierBadge = ({ tier }) => {
  if (tier === 'featured') {
    return (
      <div className="flex items-center gap-1.5 bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-md shadow-sm text-[#2C5F8D]">
        <MountainMark />
        <span className="text-[9px] font-semibold tracking-[0.2em] uppercase text-stone-700"
              style={{ fontFamily: 'Georgia, serif' }}>Featured</span>
      </div>
    );
  }
  if (tier === 'partner') {
    // Compact card variant — drop the "100" backdrop entirely (it overlaps
    // the text at this small size). Mountain mark + brand mark only. Full
    // 100-backdrop badge still renders on hero / stay pages where there's
    // more room — see FeaturedStay.js:CollectiveBadge.
    return (
      <div className="flex items-center bg-[#3a5a40]/95 backdrop-blur-sm px-2.5 py-1 rounded-md shadow-sm text-white">
        <MountainMark className="w-3.5 h-3.5 mr-1.5 text-white/95" strokeWidth={2} />
        <div className="flex flex-col leading-tight">
          <span className="text-[9px] font-semibold tracking-wide whitespace-nowrap"
                style={{ fontFamily: 'Georgia, serif' }}>GoHimalayan</span>
          <span className="text-[7px] font-medium text-white/80 tracking-[0.22em] uppercase whitespace-nowrap">Collective</span>
        </div>
      </div>
    );
  }
  if (tier === 'valley_partner') {
    return (
      <div className="flex items-center gap-1.5 bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-md shadow-sm text-[#3a5a40]">
        <MountainMark />
        <span className="text-[9px] font-semibold tracking-[0.2em] uppercase text-stone-700"
              style={{ fontFamily: 'Georgia, serif' }}>Valley Partner</span>
      </div>
    );
  }
  if (tier === 'discovered') {
    return (
      <div className="flex items-center gap-1.5 bg-stone-50/95 backdrop-blur-sm px-2.5 py-1 rounded-md shadow-sm text-[#3a5a40]">
        <MountainMark />
        <span className="text-[9px] font-semibold tracking-[0.2em] uppercase text-stone-700"
              style={{ fontFamily: 'Georgia, serif' }}>Discovered</span>
      </div>
    );
  }
  return null; // scraped / unknown — no badge per rule book
};

/**
 * HeroSlideshow — crossfades between 2..5 cottage hero images every 5s, with
 * a slow Ken Burns zoom on the active frame. Stops auto-rotating when the
 * tab is hidden to be polite to data/CPU.
 */
const HeroSlideshow = ({ frames, alt }) => {
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (frames.length <= 1) return;
    let alive = true;
    const tick = () => {
      if (!alive || document.hidden) return;
      setActive((i) => (i + 1) % frames.length);
    };
    const id = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [frames.length]);
  return (
    <>
      {frames.map((src, i) => (
        <img key={src + i}
             src={src}
             alt={alt}
             className={`gh-hero-frame ${i === active ? 'gh-active' : ''}`}
             loading={i === 0 ? 'eager' : 'lazy'}
        />
      ))}
    </>
  );
};

const CollectionLanding = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [enquireOpen, setEnquireOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/curated/collections/${slug}`);
        const j = await r.json();
        if (!alive) return;
        if (!r.ok) throw new Error(j.detail || 'Collection not found');
        setData(j);
      } catch (e) {
        if (alive) setErr(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading…</div>;
  }
  if (err || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <p className="text-stone-500 mb-4">{err || 'Collection not found'}</p>
        <Link to="/" className="text-[#2C5F8D] underline">← Back to home</Link>
      </div>
    );
  }

  const { collection, stays } = data;
  const experiences = ANGLE_EXPERIENCES[collection.angle] || [];

  // Hero slideshow: collect up to 5 unique cottage hero photos (override first
  // if the curator set hero_image on the collection itself). Crossfade between
  // them every 5s — feels alive, hints at the variety in the bundle.
  const heroFrames = (() => {
    const seen = new Set();
    const frames = [];
    if (collection.hero_image) {
      frames.push(collection.hero_image);
      seen.add(collection.hero_image);
    }
    for (const s of stays) {
      const img = _resolveImg(s);
      if (img && !seen.has(img)) {
        frames.push(img);
        seen.add(img);
        if (frames.length >= 5) break;
      }
    }
    return frames;
  })();

  return (
    <div className="min-h-screen bg-white">
      <style>{`
        @keyframes ghKenBurns {
          0%   { transform: scale(1.06) translate(0,0); }
          100% { transform: scale(1.20) translate(-2%, -1.5%); }
        }
        .gh-hero-frame {
          position: absolute; inset: 0; width: 100%; height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 1.6s ease-in-out;
          will-change: transform, opacity;
        }
        .gh-hero-frame.gh-active {
          opacity: 1;
          animation: ghKenBurns 12s ease-in-out infinite alternate;
        }
      `}</style>
      <SEOHead
        title={`${collection.name} · GoHimalayan`}
        description={collection.tagline || collection.description?.slice(0, 160)}
      />

      {/* Hero — multi-image crossfading slideshow */}
      <section className="relative h-[82vh] min-h-[560px] flex items-end overflow-hidden" data-testid="collection-hero">
        {heroFrames.length ? (
          <HeroSlideshow frames={heroFrames} alt={collection.name} />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-stone-800 to-stone-600" />
        )}
        {/* Warm + dark overlay layered for richer mood than a flat black gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/85" />
        <div className="absolute inset-0 bg-gradient-to-tr from-amber-900/15 via-transparent to-emerald-900/10 mix-blend-multiply" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 pb-16 lg:pb-24 w-full">
          {collection.badge && (
            <div className="inline-flex items-center gap-2 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-md mb-6 shadow-lg">
              <span className="w-1.5 h-1.5 rounded-full bg-[#3a5a40]" />
              <span className="text-[10px] font-semibold text-stone-700 tracking-[0.18em] uppercase"
                    style={{ fontFamily: 'Georgia, serif' }}>{collection.badge}</span>
            </div>
          )}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5 drop-shadow-lg"
              style={{ fontFamily: 'Playfair Display, serif' }} data-testid="collection-name">
            {collection.name}
          </h1>
          {collection.tagline && (
            <p className="text-lg lg:text-xl text-white/95 max-w-2xl leading-relaxed italic drop-shadow"
               style={{ fontFamily: 'Playfair Display, serif' }} data-testid="collection-tagline">
              {collection.tagline}
            </p>
          )}
          <div className="mt-8 flex items-center gap-6 text-white/90 text-sm tracking-wide">
            <span><b className="text-white text-base">{stays.length}</b> hand-picked stays</span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">Direct host bookings</span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">Zero commission, ever</span>
          </div>
        </div>
      </section>

      {/* Story */}
      {collection.description && (
        <section className="py-20 px-6 lg:px-8" data-testid="collection-story">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#2C5F8D]" />
              <p className="text-xs tracking-[0.3em] text-[#2C5F8D] uppercase">Why this collection</p>
            </div>
            {collection.description.split('\n\n').map((p, i) => (
              <p key={i} className="text-lg text-stone-700 leading-relaxed mb-5"
                 style={{ fontFamily: 'Playfair Display, serif' }}>
                {p}
              </p>
            ))}
          </div>
        </section>
      )}

      {/* Stays grid */}
      <section className="bg-stone-50 py-20 px-6 lg:px-8" data-testid="collection-stays">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-stone-400" />
            <p className="text-xs tracking-[0.3em] text-stone-500 uppercase">The cottages</p>
          </div>
          <h2 className="text-3xl font-bold text-stone-900 mb-3"
              style={{ fontFamily: 'Playfair Display, serif' }}>
            {stays.length} private homes, hand-picked
          </h2>
          <p className="text-stone-500 mb-12 max-w-2xl">
            Each one bookable whole. Tap a card to read the story, see the pictures, and message the host directly.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {stays.map((s) => {
              const path = _resolvePath(s);
              const img = _resolveImg(s);
              const sleeps = s.unit_count ? `${s.unit_count} ${s.unit_type || 'rooms'}` : null;
              const valley = s.valley || (s.display_name?.split(',')[1] || '').trim();

              // Approximate whole-cottage nightly rate. Lead with this for the
              // Family Estate Collection since the angle is "book the whole place".
              //   per_room → rooms × rate, floor-rounded to a clean ₹1k figure
              //   whole_property → use as declared
              //   per_person / unknown → show per-unit "from" rate as a fallback
              const price = Number(s.starting_price_inr) || 0;
              const rooms = Number(s.unit_count) || 0;
              let wholePrice = null;
              if (price) {
                if (s.price_unit === 'whole_property') wholePrice = price;
                else if ((s.price_unit === 'per_room' || !s.price_unit) && rooms > 0) {
                  wholePrice = Math.floor((price * rooms) / 1000) * 1000;
                }
              }
              const fmt = (n) => '₹' + Number(n).toLocaleString('en-IN');

              // Badge per the GoHimalayan rule book (matches FeaturedStay.js).
              // Every tier carries the mountain mark — only the colour & label
              // change. `scraped` and `discovered` get no badge so cards stay
              // visually clean and the eye is drawn to actual credentials.
              const tier = s.partnership_status;

              return (
                <Link key={s.gohimalayan_id} to={path}
                      className="group block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500"
                      data-testid={`collection-stay-${s.gohimalayan_id}`}>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    {img ? (
                      <img src={img} alt={s.display_name}
                           className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="w-full h-full bg-stone-200" />
                    )}
                    {/* Official tier badge — mountain mark per rule book. */}
                    <div className="absolute top-3 left-3">
                      <TierBadge tier={tier} />
                    </div>
                    {sleeps && (
                      <div className="absolute bottom-3 right-3 bg-white/95 backdrop-blur-sm text-stone-800 text-[10px] font-semibold tracking-wide uppercase px-2.5 py-1 rounded shadow-sm">
                        A stay of {sleeps}
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="text-xl font-semibold text-stone-900 leading-tight mb-1"
                        style={{ fontFamily: 'Playfair Display, serif' }}>
                      {(s.display_name || '').split(',')[0]}
                    </h3>
                    {valley && <p className="text-xs text-stone-500 tracking-wide uppercase mb-3">{valley}</p>}
                    {s.tagline && (
                      <p className="text-sm text-stone-600 italic leading-relaxed line-clamp-2"
                         style={{ fontFamily: 'Playfair Display, serif' }}>
                        {s.tagline}
                      </p>
                    )}
                    <div className="mt-4 flex items-end justify-between gap-3">
                      <div className="min-w-0">
                        {wholePrice ? (
                          <>
                            <p className="text-[10px] text-stone-400 uppercase tracking-[0.18em]">Whole cottage from</p>
                            <p className="text-base font-semibold text-stone-900">
                              {fmt(wholePrice)} <span className="text-stone-400 text-xs font-normal">/ night</span>
                            </p>
                          </>
                        ) : price ? (
                          <>
                            <p className="text-[10px] text-stone-400 uppercase tracking-[0.18em]">From</p>
                            <p className="text-base font-semibold text-stone-900">
                              {fmt(price)} <span className="text-stone-400 text-xs font-normal">/ night</span>
                            </p>
                          </>
                        ) : (
                          <p className="text-xs text-stone-400">Direct host quote</p>
                        )}
                      </div>
                      <span className="flex-shrink-0 flex items-center gap-1 text-[#2C5F8D] text-xs font-medium group-hover:gap-2 transition-all">
                        Read story <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Experiences strip — editorial "field guide" feel with hand-written
          accent labels, varied card heights, and a postcard-style header. */}
      {experiences.length > 0 && (
        <section className="relative py-24 px-6 lg:px-8 overflow-hidden" data-testid="collection-experiences">
          {/* Subtle paper-grain backdrop */}
          <div className="absolute inset-0 pointer-events-none opacity-[0.04]"
               style={{
                 backgroundImage: 'radial-gradient(circle at 25% 30%, #3a5a40 0, transparent 35%), radial-gradient(circle at 75% 60%, #8b5e34 0, transparent 35%)',
               }} />
          <div className="relative max-w-6xl mx-auto">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-2">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-px bg-[#3a5a40]" />
                  <p className="text-xs tracking-[0.32em] text-[#3a5a40] uppercase font-semibold">A Field Guide</p>
                  <div className="w-10 h-px bg-[#3a5a40]" />
                </div>
                <h2 className="text-4xl sm:text-5xl font-bold text-stone-900 leading-tight"
                    style={{ fontFamily: 'Playfair Display, serif' }}>
                  Your week, <span className="italic text-[#3a5a40]">unhurried</span>.
                </h2>
              </div>
              <p className="text-sm text-stone-500 italic max-w-sm"
                 style={{ fontFamily: 'Playfair Display, serif' }}>
                Suggestions from hosts who&rsquo;ve watched hundreds of families
                pour through these homes. No two weeks look the same.
              </p>
            </div>

            {/* Postcard grid — alternating card backgrounds + varied heights
                give the section a scrapbook-like rhythm. Each item carries a
                small hand-written label so the eye has something to grab. */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
              {experiences.map((e, i) => {
                const Ico = e.icon;
                // Asymmetric: every 5th card spans 2 cols on lg, every 7th gets a sepia accent
                const wide = i % 5 === 0 && i !== 0;
                const accent = i % 3 === 0;
                const sepia = i % 4 === 0;
                return (
                  <article
                    key={i}
                    className={[
                      'relative group rounded-2xl border p-6 sm:p-7 transition-all duration-300',
                      wide ? 'lg:col-span-2' : '',
                      accent
                        ? 'bg-[#fdfaf3] border-[#e7dfca] hover:shadow-xl hover:-translate-y-0.5'
                        : 'bg-white border-stone-200 hover:border-stone-400 hover:shadow-lg',
                      sepia ? 'shadow-[inset_0_0_60px_-30px_rgba(139,94,52,0.15)]' : '',
                    ].join(' ')}
                  >
                    {/* Soft "category" pill — cream chip, sage text, no harsh black */}
                    {e.hand && (
                      <span
                        className="absolute -top-2.5 left-5 bg-[#f4ecd8] text-[#3a5a40] text-[9px] tracking-[0.22em] uppercase px-2.5 py-1 rounded-sm border border-[#e2d6b0] shadow-sm"
                        style={{ fontFamily: 'Georgia, serif' }}
                      >
                        {e.hand}
                      </span>
                    )}
                    {/* Icon (the "sign") */}
                    <div className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#3a5a40]/8 border border-[#3a5a40]/15">
                      <Ico className="h-6 w-6 text-[#3a5a40]" strokeWidth={1.5} />
                    </div>
                    <h3 className="text-2xl text-stone-900 mb-2 leading-snug"
                        style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '-0.01em' }}>
                      {e.title}
                    </h3>
                    <p className="text-[15px] text-stone-600 leading-relaxed"
                       style={{ fontFamily: 'Georgia, serif' }}>
                      {e.copy}
                    </p>
                  </article>
                );
              })}
            </div>

            {/* Footnote line — small, italic, like an editor's signature */}
            <p className="mt-10 text-center text-xs text-stone-400 italic tracking-wide"
               style={{ fontFamily: 'Playfair Display, serif' }}>
              — most of the above can be arranged with a 24-hour heads-up. Just WhatsApp the host.
            </p>
          </div>
        </section>
      )}

      {/* Footer CTA — opens the EnquireModal in collection mode (10% discount
          path suppressed, name/city/phone mandatory, message routes to the
          GoHimalayan central WhatsApp). */}
      <section className="bg-stone-900 py-20 px-6 lg:px-8 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}>
            Not sure which cottage?
          </h2>
          <p className="text-stone-300 mb-8 leading-relaxed">
            Tell us your dates, how many you are, and what kind of week you&rsquo;re after. We&rsquo;ll send back two or three that fit — no pressure to book.
          </p>
          <button
            type="button"
            onClick={() => setEnquireOpen(true)}
            className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ea952] text-white font-semibold px-6 py-3 rounded-md transition-colors"
            data-testid="collection-whatsapp-cta"
          >
            <MessageCircle className="h-4 w-4" /> Check dates & enquire
          </button>
        </div>
      </section>

      <EnquireModal
        open={enquireOpen}
        onClose={() => setEnquireOpen(false)}
        whatsappNumber="919871155963"
        source="collection"
        contextLabel={`collection_${collection.slug}`}
        propertyShortName={collection.name}
        collectionMode={true}
        collectionName={collection.name}
      />
    </div>
  );
};

export default CollectionLanding;
